/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import { collection, query, where, orderBy, limit, updateDoc, doc, startAfter, getDocs, getCountFromServer, DocumentSnapshot} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheckCircle, faClock, faTimes } from '@fortawesome/free-solid-svg-icons';
import './css/MisCorreos.css';
import { mostrarError, mostrarExito } from '../../utils/Alertas';

interface Correo {
    id: string;
    to: string;
    subject: string;
    message: string;
    scheduledAt: any;
    estado: 'Pendiente' | 'Enviado' | 'Cancelado' | 'Error';
    remitente: string;
    creadoEn: any;
    expandido?: boolean;
}

const MisCorreos: React.FC = () => {
    const correosPorPagina = 6;
    const [correos, setCorreos] = useState<Correo[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [snapshots, setSnapshots] = useState<DocumentSnapshot[]>([]);
    const user = getAuth().currentUser;
    const [totalCorreos, setTotalCorreos] = useState(0);

    const cargarPagina = async (page: number) => {
        if (!user) return;

        let qBase = query(
            collection(db, 'emailsProgramados'),
            where('uid', '==', user.uid),
            orderBy('scheduledAt', 'desc')
        );

        let qFinal;

        if (page === 1) {
            qFinal = query(qBase, limit(correosPorPagina));
        } else {
            const lastSnap = snapshots[page - 2];
            if (!lastSnap) return;
            qFinal = query(qBase, startAfter(lastSnap), limit(correosPorPagina));
        }

        const snapshot = await getDocs(qFinal);
        const correosData: Correo[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as Omit<Correo, 'id'>),
        }));

        setCorreos(correosData);
        setCurrentPage(page);

        if (snapshot.docs.length > 0) {
            setSnapshots(prev => {
                const nuevosSnapshots = [...prev];
                nuevosSnapshots[page - 1] = snapshot.docs[snapshot.docs.length - 1];
                return nuevosSnapshots;
            });
        }
    };

    // Ejecutar una vez al montar
    useEffect(() => {
        const obtenerTotalCorreos = async () => {
            const user = getAuth().currentUser;
            if (!user) return;

            const q = query(
                collection(db, 'emailsProgramados'),
                where('uid', '==', user.uid)
            );
            const snap = await getCountFromServer(q);
            const total = snap.data().count;
            setTotalCorreos(total);
            const paginas = Math.ceil(total / correosPorPagina);
            setTotalPaginas(paginas);
        };

        obtenerTotalCorreos();
        cargarPagina(1);
    }, []);

    // Refrescar la página actual cada 30s
    useEffect(() => {
        const intervalo = setInterval(() => {
            const user = getAuth().currentUser;
            if (!user || totalPaginas === 0) return;
            cargarPagina(currentPage);
        }, 30000);

        return () => clearInterval(intervalo);
    }, [currentPage, totalPaginas]);

    const cancelarEnvio = async (id: string) => {
        try {
            await updateDoc(doc(db, 'emailsProgramados', id), {
                estado: 'Cancelado',
            });

            mostrarExito("Envío cancelado correctamente.");
            cargarPagina(currentPage);
        } catch (error: any) {
            mostrarError("Hubo un problema al cancelar el envío.");
            console.error(error);
        }
    };

    const toggleExpandido = (id: string) => {
        setCorreos((prev) =>
            prev.map((correo) =>
                correo.id === id ? { ...correo, expandido: !correo.expandido } : { ...correo, expandido: false }
            )
        );
    };

    const getEstadoIcono = (estado: string) => {
        if (estado === 'Enviado') return <FontAwesomeIcon icon={faCheckCircle} className="icono-estado verde" title="Enviado" />;
        if (estado === 'Pendiente') return <FontAwesomeIcon icon={faClock} className="icono-estado amarillo" title="Pendiente" />;
        if (estado === 'Error') return <FontAwesomeIcon icon={faTimes} className="icono-estado rojo" title="Error de envío" />;
        return <FontAwesomeIcon icon={faTimes} className="icono-estado rojo" title="Cancelado" />;
    };

    const formatDateTime = (timestamp: any) => {
        const date = new Date(timestamp?.seconds * 1000);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    return (
        <div className="container-fluid" style={{ paddingRight: 0, paddingLeft: 0, paddingBottom: 0 }}>
            <div className="lista-correos">
                <h2>correos programados</h2>
                <div className="card-list">
                    {correos.map((correo) => (
                        <div key={correo.id} className={`card-correo ${correo.expandido ? 'expandido' : ''}`}>
                            <div className="cabecera-correo" onClick={() => toggleExpandido(correo.id)}>
                                <FontAwesomeIcon icon={correo.expandido ? faChevronUp : faChevronDown} className="icono-desplegar" />
                                <span className="asunto">{correo.subject.length > 20 ? correo.subject.slice(0, 20) + '...' : correo.subject}</span>
                                <span className="fecha">{formatDateTime(correo.scheduledAt)}</span>
                                <span className="estado-icono">{getEstadoIcono(correo.estado)}</span>
                            </div>
                            {correo.expandido && (
                                <div className="contenido-expandido">
                                    <p className="text-center"><strong>{correo.subject}</strong> </p>
                                    <p><strong>Para:</strong> {correo.to}</p>
                                    <pre className="mensaje-visual">{correo.message}</pre>
                                    {correo.estado === 'Pendiente' && (
                                        <button onClick={() => cancelarEnvio(correo.id)} className="boton-cancelar">
                                            Cancelar envío
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {totalPaginas > 1 && (
                    <div className="paginacion-fixed-wrapper">
                        {totalCorreos > 0 && (
                            <div className="rango-correos">
                                Mostrando{' '}
                                {Math.min((currentPage - 1) * correosPorPagina + 1, totalCorreos)} – {Math.min(currentPage * correosPorPagina, totalCorreos)} de {totalCorreos} correos
                            </div>
                        )}
                        <div className="paginacion-bootstrap">
                            <button
                                onClick={() => cargarPagina(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                ←
                            </button>

                            {(() => {
                                const visiblePages: (number | string)[] = [];
                                const maxVisible = 5;

                                if (totalPaginas <= maxVisible) {
                                    for (let i = 1; i <= totalPaginas; i++) {
                                        visiblePages.push(i);
                                    }
                                } else {
                                    visiblePages.push(1);

                                    if (currentPage > 3) visiblePages.push('...');

                                    const start = Math.max(2, currentPage - 1);
                                    const end = Math.min(totalPaginas - 1, currentPage + 1);

                                    for (let i = start; i <= end; i++) {
                                        visiblePages.push(i);
                                    }

                                    if (currentPage < totalPaginas - 2) visiblePages.push('...');

                                    visiblePages.push(totalPaginas);
                                }

                                return visiblePages.map((page, idx) =>
                                    page === '...'
                                        ? <span key={`ellipsis-${idx}`} className="puntos-suspensivos">…</span>
                                        : (
                                            <button
                                                key={page}
                                                onClick={() => cargarPagina(Number(page))}
                                                className={currentPage === page ? 'activo' : ''}
                                            >
                                                {page}
                                            </button>
                                        )
                                );
                            })()}

                            <button
                                onClick={() => cargarPagina(currentPage + 1)}
                                disabled={currentPage === totalPaginas}
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MisCorreos;
